import { faTrain } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Icon, Tooltip } from "antd";
import React from "react";
import ReviewStatusIcon from "./ReviewStatusIcon";
import { useDispatch } from "react-redux";
import { reviewVegetationEncroachmentRisk } from "redux/actions/markerActions";

const VEGETATION_RISK_NEEDS_REVIEW = 0;
const VEGETATION_RISK_VERIFIED = 1;
const VEGETATION_RISK_REJECTED = 3;

const riskTypeMap = {
    sign_sighting: {
        title: "Sighting Risk",
        asset: "Sign",
        icon: (
            <Icon
                type="eye-invisible"
                style={{ fontSize: 24 }}
            />
        ),
    },
    signal_sighting: {
        title: "Sighting Risk",
        asset: "Signal",
        icon: (
            <Icon
                type="eye-invisible"
                style={{ fontSize: 24 }}
            />
        ),
    },
    sign_within_1m: {
        title: "Within One Meter Risk",
        asset: "Sign",
        icon: (
            <Icon
                type="column-width"
                style={{ fontSize: 24 }}
            />
        ),
    },
    signal_within_1m: {
        title: "Within One Meter Risk",
        asset: "Signal",
        icon: (
            <Icon
                type="column-width"
                style={{ fontSize: 24 }}
            />
        ),
    },
    train_contact: {
        title: "Train Contact Risk",
        asset: "Train contact",
        icon: (
            <FontAwesomeIcon
                icon={faTrain}
                style={{ width: 24, height: 24 }}
            />
        ),
    },
    next_to_ole: {
        title: "Next to OLE Risk",
        asset: "OLE",
        icon: (
            <svg
                width="113"
                height="136"
                style={{ width: 24, height: 24 }}
                viewBox="0 0 113 136"
                fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <line
                    x1="111"
                    y1="42"
                    x2="111"
                    y2="60"
                    stroke="currentColor"
                    opacity="0.75"
                    strokeWidth="4"
                />
                <rect
                    y="129"
                    width="21"
                    height="7"
                    fill="currentColor"
                />
                <line
                    x1="41"
                    y1="90"
                    x2="41"
                    y2="1"
                    stroke="currentColor"
                    opacity="0.75"
                    strokeWidth="10"
                />
                <line
                    x1="44.7979"
                    y1="9.04619"
                    x2="100.947"
                    y2="39.2385"
                    stroke="currentColor"
                    opacity="0.75"
                    strokeWidth="4"
                />
                <rect
                    x="33"
                    y="90"
                    width="22"
                    height="7"
                    stroke="currentColor"
                    opacity="0.75"
                />
                <line
                    x1="8"
                    y1="129"
                    x2="8"
                    y2="40"
                    stroke="currentColor"
                    strokeWidth="10"
                />
                <line
                    x1="80"
                    y1="80"
                    x2="13"
                    y2="80"
                    stroke="currentColor"
                    strokeWidth="10"
                />
                <line
                    x1="11.6105"
                    y1="47.6904"
                    x2="67.929"
                    y2="77.2288"
                    stroke="currentColor"
                    strokeWidth="4"
                />
                <path
                    d="M9 41C18.2288 38.7616 37.7492 27.6277 42 1"
                    stroke="currentColor"
                    strokeWidth="2"
                />
                <line
                    x1="113"
                    y1="42"
                    x2="46"
                    y2="42"
                    stroke="currentColor"
                    opacity="0.75"
                    strokeWidth="10"
                />
                <line
                    x1="78"
                    y1="81"
                    x2="78"
                    y2="100"
                    stroke="currentColor"
                    strokeWidth="4"
                />
                <path
                    d="M79 79C88.2288 76.7616 107.749 65.6277 112 39"
                    stroke="currentColor"
                    strokeWidth="2"
                />
            </svg>
        ),
    },
    overhanging: {
        title: "Overhanging Risk",
        asset: "Overhanging",
        icon: (
            <FontAwesomeIcon
                icon={faTrain}
                style={{ width: 24, height: 24 }}
            />
        ),
    },
};

export function VegetationRisksList({ extent }) {
    const dispatch = useDispatch();

    const review = (riskType, reviewStatus) => {
        dispatch(reviewVegetationEncroachmentRisk(extent.id, riskType, reviewStatus));
    };

    if (!extent?.risk_factors) {
        return null;
    }

    return (
        <div className="VegetationRisksContainer">
            <div className="Heading">Risks</div>
            <div className="VegetationRisksList">
                {Object.keys(extent.risk_factors).map((riskKey) => {
                    const riskType = riskTypeMap[riskKey];
                    if (!riskType) {
                        return null;
                    }

                    const riskReview = extent?.risk_factors_user_data?.[riskKey];
                    const reviewStatus = riskReview?.review_status ?? VEGETATION_RISK_NEEDS_REVIEW;
                    const reviewSource = riskReview?.review_source;

                    return (
                        <div
                            key={riskKey}
                            className="RisksListItem">
                            <div className="RisksListItem-Detail">
                                <div className="Icon">{riskType.icon}</div>
                                <div className="Risk">
                                    {reviewSource ? (
                                        <Tooltip title={`Reviewed by: ${reviewSource}`}>
                                            <div>
                                                <ReviewStatusIcon status={reviewStatus} />
                                            </div>
                                        </Tooltip>
                                    ) : (
                                        <ReviewStatusIcon status={reviewStatus} />
                                    )}
                                    <span className="Type">{riskType.asset}</span>
                                    <span className="Title">{riskType.title}</span>
                                </div>
                            </div>
                            <div className="RisksListItem-ReviewActions">
                                <button
                                    type="button"
                                    className={`ReviewButton VerifyButton ${reviewStatus === VEGETATION_RISK_VERIFIED ? "active" : ""}`}
                                    onClick={() => review(riskKey, VEGETATION_RISK_VERIFIED)}>
                                    {reviewStatus === VEGETATION_RISK_VERIFIED ? "Verified" : "Verify"}
                                </button>

                                <button
                                    type="button"
                                    className={`ReviewButton RejectButton ${reviewStatus === VEGETATION_RISK_REJECTED ? "active" : ""}`}
                                    onClick={() => review(riskKey, VEGETATION_RISK_REJECTED)}>
                                    {reviewStatus === VEGETATION_RISK_REJECTED ? "Rejected" : "Reject"}
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
